import "./ProfileSelectorMenu.scss";
import type { ReactNode } from "react";
import { useState } from "react";
import type { Business } from "../../../../services/MyBizShell/mybizShellAPI.types";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { showProfile } from "../../../../reducers/profile";
import { IconUser } from "../../../../common/icons/IconUser/IconUser";
import { IconWorld } from "../../../../common/icons/IconWorld/IconWorld";
import { IconResources } from "../../../../common/icons/IconResources/IconResources";
import { IconLanguages } from "../../../../common/icons/IconLanguages/IconLanguages";
import { IconFeedback } from "../../../../common/icons/IconFeedback/IconFeedback";
import { IconLogOut } from "../../../../common/icons/IconLogOut/IconLogOut";
import { IconArrowLink } from "../../../../common/icons/IconArrowLink/IconArrowLink";
import { BusinessSelector } from "./BusinessSelector/BusinessSelector";
import { ResourcesPanel } from "./ResourcesPanel/ResourcesPanel";
import { LanguageSelector } from "./LanguageSelector/LanguageSelector";
import { getProfileMenuResourceLinks } from "../../../../common/util/Navigation/profileMenuResources";
import { storedLocale } from "../../../Authentication/util";
import { ROUTE_PATHS } from "../../../../common/enums/routing-enums";
import defaultContent from "../../../../content/profileSelectorDropdown";
import { trackOpenDownlineProfile } from "../../../DownlineProfile/PlatinumProfile/downlineProfileTealium";

type SubPanel = "selectBusiness" | "resources" | "selectLanguage" | null;

interface MenuItem {
  key: string;
  icon: ReactNode;
  label: string;
  subPanel: SubPanel;
  hasChevron: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  { key: "profile", icon: <IconUser color="blue" />, label: "profile", subPanel: null, hasChevron: false },
  {
    key: "selectBusiness",
    icon: <IconWorld />,
    label: "selectBusiness",
    subPanel: "selectBusiness",
    hasChevron: true,
  },
  { key: "resources", icon: <IconResources />, label: "resources", subPanel: "resources", hasChevron: true },
  {
    key: "selectLanguage",
    icon: <IconLanguages />,
    label: "selectLanguage",
    subPanel: "selectLanguage",
    hasChevron: true,
  },
  { key: "giveFeedback", icon: <IconFeedback />, label: "giveFeedback", subPanel: null, hasChevron: false },
  { key: "logOut", icon: <IconLogOut />, label: "logOut", subPanel: null, hasChevron: false },
];

interface ProfileSelectorMenuProps {
  onClose?: () => void;
  businesses?: Business[];
  isLoading?: boolean;
  isError?: boolean;
  isKpiLoading?: boolean;
  onSelectBusiness?: (business: Business) => void;
  onBusinessSubPanelOpen?: () => void;
}

export const ProfileSelectorMenu = ({
  onClose,
  businesses = [],
  isLoading = false,
  isError = false,
  isKpiLoading = false,
  onSelectBusiness,
  onBusinessSubPanelOpen,
}: ProfileSelectorMenuProps) => {
  const { t } = useTranslation(["profileSelectorDropdown"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activePanel, setActivePanel] = useState<SubPanel>(null);
  const aboInfo = useAppSelector((state) => state.boot.shellConfig?.aboInfo);
  const user = useAppSelector((state) => state.boot.user);
  const resourceLinks = getProfileMenuResourceLinks();
  const hasResources = resourceLinks.length > 0;
  const hasMultipleBusinesses = businesses.length > 1;
  const visibleMenuItems = MENU_ITEMS.filter(
    (item) => (item.key !== "resources" || hasResources) && (item.key !== "selectBusiness" || hasMultipleBusinesses),
  );
  const menuClassName = "ProfileSelectorMenu";

  const { pathname } = useLocation();
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const isEnabled = profile?.enableDownlineProfile ?? false;
  const profileParam = new URLSearchParams(window.location.search).get("profile");
  const isLegacyOverride = profileParam === "legacy";
  const useDownlineProfile = (isEnabled || profileParam === "downline") && !isLegacyOverride;

  if (activePanel === "selectBusiness") {
    return (
      <div className={menuClassName}>
        <BusinessSelector
          onBack={() => setActivePanel(null)}
          businesses={businesses}
          isLoading={isLoading}
          isError={isError}
          isKpiLoading={isKpiLoading}
          onSelectBusiness={onSelectBusiness}
        />
      </div>
    );
  }

  if (activePanel === "resources") {
    return (
      <div className={`${menuClassName} ProfileSelectorMenu--resources`}>
        <ResourcesPanel links={resourceLinks} onBack={() => setActivePanel(null)} />
      </div>
    );
  }

  if (activePanel === "selectLanguage") {
    return (
      <div className={menuClassName}>
        <LanguageSelector activeLocale={storedLocale()} onBack={() => setActivePanel(null)} />
      </div>
    );
  }

  const handleItemClick = (item: MenuItem) => {
    if (item.key === "profile") {
      dispatch(showProfile(`${user.aff}-${user.abo}`));
      if (useDownlineProfile) {
        trackOpenDownlineProfile(pathname);
      }
      onClose?.();
      return;
    }
    if (item.key === "giveFeedback") {
      navigate(ROUTE_PATHS.FEEDBACK);
      onClose?.();
      return;
    }
    if (item.key === "logOut") {
      navigate(ROUTE_PATHS.LOG_OUT);
      return;
    }
    if (item.subPanel) {
      if (item.subPanel === "selectBusiness") {
        onBusinessSubPanelOpen?.();
      }
      setActivePanel(item.subPanel);
    }
  };

  const getItemLabel = (item: MenuItem): string => {
    return t(item.label, defaultContent[item.label]);
  };

  return (
    <div className={menuClassName} role="menu" data-testid="profile-selector-menu">
      {visibleMenuItems.map((item) => (
        <button
          key={item.key}
          type="button"
          className="ProfileSelectorMenu__item"
          role="menuitem"
          data-testid={`profile-menu-item-${item.key}`}
          onClick={() => handleItemClick(item)}
        >
          <span className="ProfileSelectorMenu__item-left">
            <span className="ProfileSelectorMenu__item-icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="ProfileSelectorMenu__item-label">{getItemLabel(item)}</span>
          </span>
          {item.hasChevron && (
            <span className="ProfileSelectorMenu__item-chevron" aria-hidden="true">
              <IconArrowLink iconColor="blue" weight="thin" />
            </span>
          )}
        </button>
      ))}
    </div>
  );
};
